import React, { useState, useEffect } from 'react';
import {
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    ClockIcon,
    ServerIcon,
    CircleStackIcon,
    DocumentDuplicateIcon,
    TableCellsIcon,
    CheckCircleIcon,
    XCircleIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';

interface BackupHistory {
    id: number;
    tenant_id: number | null;
    backup_type: string;
    status: string;
    file_size_bytes: number | null;
    created_at: string;
    completed_at: string | null;
    details: any;
}

interface BackupRestoreProps {
    apiFetch: (endpoint: string, options?: RequestInit) => Promise<any>;
    currentTenantId: number | null;
    currentUser: any;
}

const BackupRestore: React.FC<BackupRestoreProps> = ({ apiFetch, currentTenantId, currentUser }) => {
    const [activeTab, setActiveTab] = useState<'create' | 'history'>('create');
    const [histories, setHistories] = useState<BackupHistory[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

    // For Creation Form
    const [backupType, setBackupType] = useState<'full' | 'snapshot' | 'differential' | 'partial'>('snapshot');
    const [sinceDate, setSinceDate] = useState<string>('');
    const [selectedTables, setSelectedTables] = useState<string[]>(['mt_kamoku', 'journal_entries', 'kaikei_denpyou_meisai']);

    const isSuperAdmin = currentUser?.global_role === 'super_admin';

    const fetchHistory = async () => {
        try {
            if (!currentTenantId && !isSuperAdmin) return;
            const url = currentTenantId
                ? `/api/backup/history?tenant_id=${currentTenantId}`
                : `/api/backup/history`;
            const data = await apiFetch(url);
            setHistories(data);
        } catch (error) {
            console.error('Failed to fetch backup history', error);
        }
    };

    useEffect(() => {
        fetchHistory();
        // Poll history every 10 seconds if there are running backups
        const interval = setInterval(() => {
            setHistories(prev => {
                const hasRunning = prev.some(h => h.status === 'running');
                if (hasRunning) {
                    fetchHistory();
                }
                return prev;
            });
        }, 10000);
        return () => clearInterval(interval);
    }, [currentTenantId]);

    const handleCreateBackup = async () => {
        if (backupType !== 'full' && !currentTenantId) {
            setStatusMessage({ type: 'error', text: 'テナントが選択されていません。' });
            return;
        }

        setIsLoading(true);
        setStatusMessage(null);

        const requestData = {
            backup_type: backupType,
            tenant_id: (backupType !== 'full') ? currentTenantId : null,
            tables: backupType === 'partial' ? selectedTables : null,
            since_date: backupType === 'differential' ? (sinceDate || undefined) : null,
        };

        try {
            await apiFetch('/api/backup/advanced', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });
            setStatusMessage({ type: 'info', text: 'バックアップ処理をバックグラウンドで開始しました。履歴タブで進行状況を確認できます。' });
            setActiveTab('history');
            fetchHistory();
        } catch (error: any) {
            setStatusMessage({ type: 'error', text: error.message || 'バックアップ処理の開始に失敗しました。' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async (history: BackupHistory) => {
        try {
            setIsLoading(true);
            const url = `/api/backup/download/${history.id}`;
            // Direct download using fetch and blob to handle authentication (apiFetch)
            const token = localStorage.getItem('token');
            const response = await fetch(process.env.REACT_APP_API_URL + url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('ダウンロードに失敗しました');
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = downloadUrl;
            const filename = response.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') || `backup_${history.id}.zip`;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            a.remove();
        } catch (error: any) {
            setStatusMessage({ type: 'error', text: error.message || 'ダウンロードに失敗しました。' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleRestore = async (historyId: number) => {
        if (!window.confirm('現在のデータは上書きされ復元されます。復元処理を開始してよろしいですか？')) {
            return;
        }

        setIsLoading(true);
        setStatusMessage(null);

        try {
            await apiFetch(`/api/backup/restore/${historyId}`, {
                method: 'POST'
            });
            setStatusMessage({ type: 'success', text: 'リストア（復元）処理が正常に完了しました。システムの設定を反映させるため、再度ログインすることをお勧めします。' });
        } catch (error: any) {
            setStatusMessage({ type: 'error', text: error.message || 'リストア処理に失敗しました。' });
        } finally {
            setIsLoading(false);
        }
    };

    const formatSize = (bytes: number | null) => {
        if (bytes === null) return '-';
        if (bytes < 1024) return bytes + ' B';
        else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        else return (bytes / 1048576).toFixed(1) + ' MB';
    };

    const getBackupTypeName = (type: string) => {
        switch (type) {
            case 'full': return 'システムフルバックアップ';
            case 'snapshot': return '全体スナップショット';
            case 'differential': return '差分バックアップ';
            case 'partial': return '部分バックアップ';
            default: return type;
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed': return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
            case 'failed': return <XCircleIcon className="w-5 h-5 text-red-500" />;
            case 'running': return <ArrowPathIcon className="w-5 h-5 text-blue-500 animate-spin" />;
            default: return null;
        }
    };

    return (
        <div className="space-y-6 animate-fade-in pb-12">
            {/* Header */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-2">
                    <ServerIcon className="w-6 h-6 text-indigo-600" />
                    データバックアップ・復元管理
                </h2>
                <p className="text-sm text-slate-500">
                    システムデータの保護、ロールバック地点の作成、およびデータエクスポートを行います。
                    {isSuperAdmin && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">特権管理者モード</span>}
                </p>
            </div>

            {statusMessage && (
                <div className={`p-4 rounded-lg flex items-start gap-3 ${statusMessage.type === 'success' ? 'bg-green-50 text-green-800 border-l-4 border-green-500' :
                        statusMessage.type === 'error' ? 'bg-red-50 text-red-800 border-l-4 border-red-500' :
                            'bg-blue-50 text-blue-800 border-l-4 border-blue-500'
                    }`}>
                    {statusMessage.type === 'success' && <CheckCircleIcon className="w-5 h-5 mt-0.5" />}
                    {statusMessage.type === 'error' && <XCircleIcon className="w-5 h-5 mt-0.5" />}
                    {statusMessage.type === 'info' && <ClockIcon className="w-5 h-5 mt-0.5" />}
                    <p>{statusMessage.text}</p>
                </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <button
                        onClick={() => setActiveTab('create')}
                        className={`${activeTab === 'create'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                    >
                        <CircleStackIcon className="w-5 h-5" />
                        新規バックアップ
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`${activeTab === 'history'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                    >
                        <ClockIcon className="w-5 h-5" />
                        バックアップ履歴・復元
                    </button>
                </nav>
            </div>

            {/* Tab content: Create Backup */}
            {activeTab === 'create' && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                    <div className="max-w-3xl space-y-6">

                        <fieldset>
                            <legend className="text-base font-medium text-slate-800 mb-4">バックアップ方式の選択</legend>
                            <div className="space-y-4">
                                <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${backupType === 'snapshot' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                                    <input type="radio" className="mt-1 flex-shrink-0 text-indigo-600" checked={backupType === 'snapshot'} onChange={() => setBackupType('snapshot')} />
                                    <div className="ml-3">
                                        <span className="block text-sm font-medium text-slate-800">全体スナップショット (Snapshot)</span>
                                        <span className="block text-sm text-slate-500 mt-1">現在のテナント内の全データをひとつのZIPファイルとして保存します。ロールバック地点として最適です。</span>
                                    </div>
                                </label>

                                <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${backupType === 'differential' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                                    <input type="radio" className="mt-1 flex-shrink-0 text-indigo-600" checked={backupType === 'differential'} onChange={() => setBackupType('differential')} />
                                    <div className="ml-3">
                                        <span className="block text-sm font-medium text-slate-800">差分バックアップ (Differential)</span>
                                        <span className="block text-sm text-slate-500 mt-1">指定した日時以降に更新されたデータのみを保存します。日々の定期的な保存に適しています。</span>
                                    </div>
                                </label>

                                <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${backupType === 'partial' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                                    <input type="radio" className="mt-1 flex-shrink-0 text-indigo-600" checked={backupType === 'partial'} onChange={() => setBackupType('partial')} />
                                    <div className="ml-3">
                                        <span className="block text-sm font-medium text-slate-800">部分バックアップ (Partial)</span>
                                        <span className="block text-sm text-slate-500 mt-1">マスタや仕訳など、指定したテーブルのデータのみを抜き出して保存します。</span>
                                    </div>
                                </label>

                                {isSuperAdmin && (
                                    <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${backupType === 'full' ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                                        <input type="radio" className="mt-1 flex-shrink-0 text-red-600 focus:ring-red-500" checked={backupType === 'full'} onChange={() => setBackupType('full')} />
                                        <div className="ml-3">
                                            <span className="block text-sm font-medium text-slate-800 flex items-center gap-2">システムフルバックアップ <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded">SuperAdmin専用</span></span>
                                            <span className="block text-sm text-slate-500 mt-1">全テナントを含むシステムデータベース全体（PostgreSQLダンプまたはSQLite）を保存します。</span>
                                        </div>
                                    </label>
                                )}
                            </div>
                        </fieldset>

                        {/* Differential Specific Options */}
                        {backupType === 'differential' && (
                            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 ml-8 animate-fade-in">
                                <label className="block text-sm font-medium text-slate-700 mb-1">抽出基準日 (Since Date)</label>
                                <input
                                    type="date"
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    value={sinceDate}
                                    onChange={(e) => setSinceDate(e.target.value)}
                                />
                                <p className="mt-2 text-xs text-slate-500">この日時以降に `updated_at` が更新されたレコードのみが対象となります。</p>
                            </div>
                        )}

                        {/* Partial Specific Options */}
                        {backupType === 'partial' && (
                            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 ml-8 animate-fade-in">
                                <label className="block text-sm font-medium text-slate-700 mb-2">対象データ</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {['mt_kamoku', 'mt_tekiyou', 'mt_daikubun', 'journal_entries', 'kaikei_denpyou_meisai', 'fixed_assets', 'mt_budget_version'].map(table => (
                                        <label key={table} className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                                checked={selectedTables.includes(table)}
                                                onChange={(e) => {
                                                    if (e.target.checked) setSelectedTables([...selectedTables, table]);
                                                    else setSelectedTables(selectedTables.filter(t => t !== table));
                                                }}
                                            />
                                            <span className="ml-2 text-sm text-slate-700">{table}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="pt-4 border-t border-slate-200 flex justify-end">
                            <button
                                onClick={handleCreateBackup}
                                disabled={isLoading || (backupType !== 'full' && !currentTenantId)}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 transition-colors shadow-sm"
                            >
                                {isLoading ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : <DocumentDuplicateIcon className="w-5 h-5" />}
                                バックアップ実行
                            </button>
                        </div>
                        {(!currentTenantId && backupType !== 'full') && (
                            <p className="text-right text-xs text-red-500 mt-2">※テナントを選択してください。</p>
                        )}
                    </div>
                </div>
            )}

            {/* Tab content: History & Restore */}
            {activeTab === 'history' && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="flex justify-between items-center p-4 border-b border-slate-200 bg-slate-50">
                        <h3 className="font-medium text-slate-800 flex items-center gap-2">
                            <TableCellsIcon className="w-5 h-5 text-slate-500" />
                            履歴一覧
                        </h3>
                        <button
                            onClick={fetchHistory}
                            disabled={isLoading}
                            className="text-slate-500 hover:text-indigo-600 flex items-center gap-1 text-sm bg-white border border-slate-200 px-3 py-1.5 rounded disabled:opacity-50"
                        >
                            <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                            更新
                        </button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">状態</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">作成日時</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">方式</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">サイズ</th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">アクション</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-200">
                                {histories.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-8 text-center text-sm text-slate-500 border-b">
                                            バックアップ履歴が存在しません。
                                        </td>
                                    </tr>
                                ) : (
                                    histories.map((row) => (
                                        <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    {getStatusIcon(row.status)}
                                                    <span className="text-sm text-slate-700 capitalize">{row.status}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-slate-900">{new Date(row.created_at).toLocaleString('ja-JP')}</div>
                                                <div className="text-xs text-slate-500">{row.completed_at ? '完了済' : '処理中'}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                                    {getBackupTypeName(row.backup_type)}
                                                </span>
                                                {(row.backup_type === 'partial' || row.backup_type === 'differential') && row.details && (
                                                    <div className="text-[10px] text-slate-500 mt-1 max-w-[150px] truncate" title={JSON.stringify(row.details)}>
                                                        {JSON.stringify(row.details).replace(/[{}"\[\]]/g, ' ')}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                                                {formatSize(row.file_size_bytes)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                <div className="flex justify-center gap-2">
                                                    <button
                                                        onClick={() => handleDownload(row)}
                                                        disabled={row.status !== 'completed' || isLoading}
                                                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded flex items-center gap-1 disabled:opacity-50 transition-colors"
                                                        title="ダウンロード"
                                                    >
                                                        <ArrowDownTrayIcon className="w-4 h-4" />
                                                        <span className="hidden sm:inline">DL</span>
                                                    </button>

                                                    <button
                                                        onClick={() => handleRestore(row.id)}
                                                        disabled={row.status !== 'completed' || row.backup_type === 'full' || isLoading}
                                                        className="bg-amber-100 hover:bg-amber-200 text-amber-800 px-3 py-1.5 rounded flex items-center gap-1 disabled:opacity-50 transition-colors"
                                                        title={row.backup_type === 'full' ? 'フルダンプのリストアはサーバーで直接行ってください' : 'システムに復元'}
                                                    >
                                                        <ArrowUpTrayIcon className="w-4 h-4" />
                                                        <span className="hidden sm:inline">リストア</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BackupRestore;
